/** The learner's notebook: a private, always-available scratchpad for jotting
 *  down grammar rules, words, and doubts while studying. Typed notes only for
 *  now (the client's decision — "Notebook first, typing is enough"). Every
 *  note belongs to the signed-in user; the backend enforces that. */
import { useEffect, useState } from "react";
import { createNote, deleteNote, listNotes, Note, updateNote } from "../../api/notebook";
import { ApiError } from "../../api/http";
import { Button, Card, EmptyState, Spinner } from "../../components/ui";
import { useToast } from "../../components/ToastContext";
import { useT } from "../../i18n";

const TEXTAREA_CLASS =
  "w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink outline-none transition-colors placeholder:text-muted focus:border-brand";

export function NotebookPage() {
  const t = useT();
  const { push } = useToast();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");

  useEffect(() => {
    listNotes()
      .then(setNotes)
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  const fail = (err: unknown) =>
    push("error", err instanceof ApiError ? err.message : t("common.error.generic"));

  // The Add button is disabled while the draft is blank, so `content` is
  // always non-empty here.
  const add = async () => {
    setSaving(true);
    try {
      const note = await createNote(draft.trim());
      setNotes((prev) => [note, ...prev]);
      setDraft("");
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (note: Note) => {
    setEditingId(note.id);
    setEditText(note.content);
  };

  const saveEdit = async (id: string) => {
    try {
      const updated = await updateNote(id, editText.trim());
      setNotes((prev) => prev.map((n) => (n.id === id ? updated : n)));
      setEditingId(null);
    } catch (err) {
      fail(err);
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteNote(id);
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      fail(err);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold">{t("notebook.title")}</h1>
        <p className="mt-1 text-sm text-muted">{t("notebook.subtitle")}</p>
      </div>

      <Card>
        <div className="space-y-3">
          <textarea
            className={TEXTAREA_CLASS}
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("notebook.placeholder")}
            aria-label={t("notebook.placeholder")}
          />
          <div className="flex justify-end">
            <Button onClick={add} loading={saving} disabled={!draft.trim()}>
              {t("notebook.add")}
            </Button>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="grid place-items-center py-8">
          <Spinner />
        </div>
      ) : notes.length === 0 ? (
        <EmptyState message={t("notebook.empty")} />
      ) : (
        <ul className="space-y-3">
          {notes.map((note) => (
            <li key={note.id}>
              <Card>
                {editingId === note.id ? (
                  <div className="space-y-3">
                    <textarea
                      className={TEXTAREA_CLASS}
                      rows={3}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      aria-label={t("notebook.edit")}
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => setEditingId(null)}>
                        {t("common.cancel")}
                      </Button>
                      <Button onClick={() => saveEdit(note.id)} disabled={!editText.trim()}>
                        {t("common.save")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-4">
                    <p className="whitespace-pre-wrap text-sm text-ink">{note.content}</p>
                    <div className="flex shrink-0 gap-2">
                      <Button variant="ghost" onClick={() => beginEdit(note)}>
                        {t("notebook.edit")}
                      </Button>
                      <Button variant="ghost" onClick={() => remove(note.id)}>
                        {t("notebook.delete")}
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
